import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    inject,
    signal,
    viewChild
} from '@angular/core';
import {AccountSegmentStatisticsCalculator, PortfolioSegmentStatistics} from '../../data/portfolio-segment-statistics';
import {PercentPipe} from '@angular/common';
import {WjChartModule, WjFlexChart, WjFlexPie} from '@mescius/wijmo.angular2.chart';
import {Wijmo} from '../../lib/wjgrid-utils';
import {CollectionView, Globalize} from '@mescius/wijmo';
import {groupBy} from 'es-toolkit';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {AccountData, PortfolioPeriodEnum} from '../../../api-client-kt/src';
import {MathHelper} from '../../lib/math';
import {PfReportDataHelper, UwStrategyBucketTotal} from '../../data/portfolio-segment-report-data';
import {FlexChart, FlexChartBase, FlexPie} from '@mescius/wijmo.chart';
import {WjCharts} from '../../lib/wjcharts';
import {AccountGridComponent, AccountGridFilterCondition} from '../account-grid/account-grid.component';
import {ActivatedRoute, Router} from '@angular/router';

/**
 * Component used to display an overview of portfolio segment data in various chart formats.
 *
 * Features:
 * - Aimed at presenting key metrics, statistics, and visualizations related to portfolio segments.
 * - Provides predefined chart palettes and formatters for visual consistency within the Generali framework.
 *
 * Key Functionalities:
 * - Displays statistical information about portfolio segments, including renewal rates and lost/cancelled rates.
 * - Dynamically updates pie charts and bar charts with portfolio segment data.
 * - Offers toggling functionality for selecting and modifying the portfolio period context.
 *
 */
@Component({
    selector: 'app-segment-overview',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        PercentPipe,
        WjChartModule
    ],
    templateUrl: './segment-overview.component.html',
    styleUrl: './segment-overview.component.scss'
})
export class SegmentOverviewComponent {

    protected readonly Wijmo = Wijmo;

    protected statistics = signal<PortfolioSegmentStatistics|undefined>(undefined);
    protected renewedRate = signal<number>(0);
    protected lostCancelledRate = signal<number>(0);

    protected readonly chartData = new CollectionView( [
        { category: 'Expiring', amount: 210_000_000 },
        { category: 'Renewed', amount: 190_000_000},
    ]);

    protected readonly GeneraliPieChartPalette = WjCharts.GeneraliPieChartPalette;
    protected readonly GeneraliColumnChartPaletteItemFormatter = WjCharts.GeneraliColumnChartPaletteItemFormatter;
    protected readonly GeneraliBarChartPaletteItemFormatter = WjCharts.GeneraliBarChartPaletteItemFormatter;

    private readonly bucketPieChart = viewChild<WjFlexPie>('bucketPieChart');
    private readonly bucketFinalPieChart = viewChild<WjFlexPie>('bucketFinalPieChart');

    private readonly renewalCountsBarChart = viewChild<WjFlexChart>('renewalCountsBarChart');
    private readonly deltasBarChart = viewChild<WjFlexChart>('deltasBarChart');
    private readonly atsHistogramChart = viewChild<WjFlexChart>('atsHistogramChart');

    protected readonly pfSegData = inject(PortfolioSegmentDataContext);
    protected readonly period = signal<PortfolioPeriodEnum|undefined|null>(this.pfSegData.portfolio.value()?.period);

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    constructor() {
        effect(() => this.refreshStatisticsWithPortfolioData());
    }

    protected togglePeriod(period: PortfolioPeriodEnum | undefined) {
        if (this.period() === period) this.period.set(undefined);
        else this.period.set(period);
    }

    private refreshStatisticsWithPortfolioData() {
        this.pfSegData.ensureEffectOnFormulaEvaluation();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());
        const accounts = this.pfSegData.getAccountDataInFilterPeriod();

        const stats = AccountSegmentStatisticsCalculator.fromAccounts(accounts);

        this.renewedRate.set(MathHelper.safeDiv(stats.renewedCount, stats.totalCount));
        this.lostCancelledRate.set(MathHelper.safeDiv(stats.lostCancelledCount, stats.totalCount));

        const totals = PfReportDataHelper.findSummaryRow(summary, UwStrategyBucketTotal);
        this.chartData.deferUpdate(() => {
            this.chartData.items[0].amount = stats.totalGnwpEur;
            this.chartData.items[1].amount = stats.renewedGnwpEur;
        });
        this.statistics.set(stats);

        function countsBy(selector: (a: AccountData) => string) : {name: string, count: number}[] {
            const byBucket = groupBy(accounts, selector);
            return Object.keys(byBucket).map(key => {
                return {
                    name: key,
                    count: byBucket[key].length
                }
            });
        }

        function setChartData(chart: FlexChartBase | undefined, data: any[]) {
            if (chart) chart.itemsSource = data;
        }

        function setPieChartData(chart: WjFlexPie | undefined, selector: (a: AccountData) => string) {
            setChartData(chart, countsBy(selector));
        }

        setPieChartData(this.bucketPieChart(),(a => a.calculatedPilStatus ?? 'none'));
        setPieChartData(this.bucketFinalPieChart(),(a => a.finalPilStatus ?? 'none'));

        const renewalCounts = [
            { name: 'Not Renewable', count: stats.notRenewableCount },
            { name: 'Lost/Cancelled', count: stats.lostCancelledCount },
            { name: 'Renewed', count: stats.renewedCount },
            { name: 'Expiring', count: stats.totalCount },
        ];

        setChartData(this.renewalCountsBarChart(), renewalCounts);

        const deltas = summary
            .filter(it => !/not renewable/i.test(it.bucket))
            .map(it => ({ name: it.bucket, value: it.fyPlrDelta }))
            .reverse();

        setChartData(this.deltasBarChart(), deltas);

        const ats = accounts
            .filter(it => it.renewableFlag)
            .map(it => it.scoreAts)
            .filter(it => it !== undefined) as number[];

        const atsBuckets = PfReportDataHelper.autoHistogramWithVariableBucketSize(ats, 8);
        const atsBucketData = atsBuckets.map(it => {
            const label = `${Globalize.formatNumber(it.min, 'p1')} - ${Globalize.formatNumber(it.max, 'p1')}`;
            return {
                name: label,
                count: it.count,
                min: it.min,
                max: it.max
            }
        });

        setChartData(this.atsHistogramChart(), atsBucketData);
    }

    protected async onChartClick(binding: string, chart: FlexChart | FlexPie, event: MouseEvent) {
        const ht = chart.hitTest(event);
        const item = ht.item;

        if (item && ht.distance === 0) {
            if (chart instanceof FlexChart) {
                const filter: AccountGridFilterCondition<string> = { column: binding, type: 'range', value1: item.min, value2: item.max };
                await AccountGridComponent.applyFilterAndShow(this.router, this.route, filter);
            } else if (chart instanceof FlexPie) {
                const filter: AccountGridFilterCondition<string> = { column: binding, type: 'equals', value1: item.name };
                await AccountGridComponent.applyFilterAndShow(this.router, this.route, filter);
            }
        }
    }
}
